import {
    isRole,
    type Permission,
    type Role,
} from '@precon-tools-suite-by-shared/js-precon-rbac';
import type {
    AbilityContext,
    AbilityGeneratorResult,
    AbilityResult,
    AbilityReturnResult,
    AsyncAbilityGeneratorResult,
    AsyncAbilityResult,
    AuthorizationResult,
    RoleOrPermission,
} from '../types';
import { toArray, isPromiseLike } from '@precon-tools-suite-by-shared/js-utils';
import { partition } from 'lodash';

function splitRolesAndPermissions(rolesPlusPermissions: RoleOrPermission[]): {
    roles: Role[];
    permissions: Permission[];
} {
    const [roles, permissions] = partition(rolesPlusPermissions, isRole);
    return {
        roles: roles as Role[],
        permissions: permissions as Permission[],
    };
}

function createAuthorizationResultFromReturnResult(
    context: Pick<AbilityContext, 'has' | 'hasRole' | 'hasPermission'>,
    result: AbilityReturnResult | void | null | undefined
): AuthorizationResult {
    if (!result) {
        return {
            allowed: false,
            missing: [],
        };
    }

    if (result === true) {
        return { allowed: true };
    }

    const toCheck = toArray(result) as RoleOrPermission[];
    if (context.has(...toCheck)) {
        return { allowed: true };
    }

    const { roles, permissions } = splitRolesAndPermissions(toCheck);
    const nextMissing = {
        roles: roles.filter((role) => !context.hasRole(role)),
        permissions: permissions.filter(
            (permissions) => !context.hasPermission(permissions)
        ),
    };

    return {
        allowed: false,
        missing: [nextMissing],
    };
}

export function createAuthorizationResultSync(
    context: Pick<AbilityContext, 'has' | 'hasRole' | 'hasPermission'>,
    result: AbilityResult
): AuthorizationResult {
    if (
        !result ||
        typeof result === 'boolean' ||
        typeof result === 'string' ||
        Array.isArray(result)
    ) {
        return createAuthorizationResultFromReturnResult(context, result);
    } else {
        const missing: Array<{ roles: Role[]; permissions: Permission[] }> = [];
        let i: IteratorResult<
            AbilityReturnResult,
            AbilityReturnResult | null | void | undefined
        >;
        do {
            i = result.next();
            const subResult = createAuthorizationResultFromReturnResult(
                context,
                i.value
            );
            if (subResult.allowed) {
                result.return();
                return subResult;
            } else {
                missing.push(...subResult.missing);
            }
        } while (!i.done);
        return {
            allowed: false,
            missing,
        };
    }
}

function createAuthorizationResultFromGeneratorResult(
    context: Pick<AbilityContext, 'has' | 'hasRole' | 'hasPermission'>,
    result: AbilityGeneratorResult | AsyncAbilityGeneratorResult,
    missing: Array<{ roles: Role[]; permissions: Permission[] }>
): AuthorizationResult | Promise<AuthorizationResult> {
    const handleNext = (
        sub: IteratorResult<
            AbilityReturnResult,
            AbilityReturnResult | null | void | undefined
        >
    ) => {
        const subResult = createAuthorizationResultFromReturnResult(
            context,
            sub.value
        );
        if (subResult.allowed) {
            result.return();
            return subResult;
        }

        missing.push(...subResult.missing);
        if (sub.done) {
            return {
                allowed: false,
                missing,
            };
        } else {
            return createAuthorizationResultFromGeneratorResult(
                context,
                result,
                missing
            );
        }
    };

    const next = result.next();
    if (isPromiseLike(next)) {
        return next.then(handleNext);
    } else {
        return handleNext(next);
    }
}

export function createAuthorizationResult(
    context: Pick<AbilityContext, 'has' | 'hasRole' | 'hasPermission'>,
    result: AbilityResult
): AuthorizationResult;
export function createAuthorizationResult(
    context: Pick<AbilityContext, 'has' | 'hasRole' | 'hasPermission'>,
    result: AsyncAbilityResult
): Promise<AuthorizationResult>;
export function createAuthorizationResult(
    context: Pick<AbilityContext, 'has' | 'hasRole' | 'hasPermission'>,
    result: AbilityResult | AsyncAbilityResult
): AuthorizationResult | Promise<AuthorizationResult> {
    if (
        !result ||
        typeof result === 'boolean' ||
        typeof result === 'string' ||
        Array.isArray(result)
    ) {
        return createAuthorizationResultFromReturnResult(context, result);
    }

    if (isPromiseLike(result)) {
        return result.then((resolvedResult) =>
            createAuthorizationResultFromReturnResult(context, resolvedResult)
        );
    }

    return createAuthorizationResultFromGeneratorResult(context, result, []);
}
